import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

/**
 * Walk-and-Talk recorder — mic-button + timer + auto-upload to Whisper.
 * On success, parent receives onTranscribed(transcript) so it can refresh
 * the lead's notes (the backend already prepends the transcript to lead.notes).
 *
 * Props:
 *   - leadId (required)
 *   - onTranscribed?(text)  — fired after backend returns
 *   - compact? (boolean)    — small variant for mobile lead cards
 */
export default function VoiceNoteRecorder({ leadId, onTranscribed, compact = false }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [lastTranscript, setLastTranscript] = useState("");
  const [permError, setPermError] = useState("");

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setSupported(false);
    }
    return () => stopTracks();
  }, []);

  const stopTracks = () => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const start = async () => {
    setPermError("");
    setLastTranscript("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Prefer webm/opus, fall back to default
      let opts = {};
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        opts = { mimeType: "audio/webm;codecs=opus" };
      } else if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported("audio/webm")) {
        opts = { mimeType: "audio/webm" };
      }
      const rec = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const dur = Date.now() - startedAtRef.current;
        const mime = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        stopTracks();
        setRecording(false);
        setElapsedMs(0);
        if (!blob.size) {
          toast.error("No audio captured");
          return;
        }
        await uploadBlob(blob, dur, mime);
      };
      rec.start();
      recRef.current = rec;
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
      tickRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 250);
    } catch (e) {
      setPermError(e?.message || "Microphone access denied");
      stopTracks();
    }
  };

  const stop = () => {
    try {
      recRef.current?.state === "recording" && recRef.current.stop();
    } catch {
      /* noop */
    }
  };

  const uploadBlob = async (blob, durationMs, mime) => {
    setUploading(true);
    try {
      const token = localStorage.getItem("medix_admin_token");
      const ext = mime.includes("webm") ? "webm" : mime.includes("ogg") ? "ogg" : "wav";
      const fd = new FormData();
      fd.append("file", blob, `voice-${Date.now()}.${ext}`);
      fd.append("duration_ms", String(durationMs));
      fd.append("language", "en");
      const res = await fetch(`${API}/admin/leads/${leadId}/voice-note`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setLastTranscript(data.transcript || "");
      toast.success("Voice note transcribed");
      onTranscribed?.(data.transcript || "");
    } catch (e) {
      toast.error(e.message || "Voice note failed");
    } finally {
      setUploading(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
        Your browser doesn't support voice recording. Try Chrome or Safari.
      </div>
    );
  }

  const secs = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  const busy = recording || uploading;

  if (compact) {
    return (
      <button
        type="button"
        onClick={recording ? stop : start}
        disabled={uploading}
        data-testid={`voice-rec-compact-${leadId}`}
        className={`flex h-10 w-10 items-center justify-center rounded-full shadow-md transition-all
          ${recording ? "bg-rose-500 text-white animate-pulse" : "bg-[#0A2540] text-white"}
          ${uploading ? "opacity-60" : ""}`}
        aria-label={recording ? "Stop recording" : "Record voice note"}
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : recording ? (
          <Square className="h-4 w-4" fill="white" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </button>
    );
  }

  return (
    <div
      className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50/50 p-4"
      data-testid="voice-recorder"
    >
      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={recording ? stop : start}
          disabled={uploading}
          data-testid={recording ? "voice-stop" : "voice-start"}
          className={`h-11 gap-2 ${
            recording
              ? "bg-rose-500 hover:bg-rose-600 text-white animate-pulse"
              : "bg-[#0A2540] hover:bg-[#1E3A5F] text-white"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Transcribing…
            </>
          ) : recording ? (
            <>
              <Square className="h-4 w-4" fill="white" />
              Stop &amp; transcribe
            </>
          ) : (
            <>
              <Mic className="h-4 w-4" />
              Walk &amp; Talk
            </>
          )}
        </Button>

        <div className="min-w-0 flex-1">
          {recording ? (
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full bg-rose-500 animate-pulse"
                aria-hidden
              />
              <span className="font-mono-clock text-sm text-[#0A2540]">
                {mm}:{ss}
              </span>
              <span className="text-xs text-slate-500">Recording… tap stop when done.</span>
            </div>
          ) : uploading ? (
            <span className="text-xs text-slate-500">
              Sending to Whisper for transcription…
            </span>
          ) : (
            <div className="text-xs text-slate-600">
              <Sparkles className="mr-1 inline h-3 w-3 text-[#C9A961]" />
              Tap, talk, and we'll auto-add the transcript to this lead's notes.
            </div>
          )}
        </div>
      </div>

      {permError && (
        <div className="mt-2 rounded-md bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-700">
          <AlertCircle className="mr-1 inline h-3 w-3" />
          {permError}. Check browser microphone permission.
        </div>
      )}

      {lastTranscript && !recording && !uploading && (
        <div
          className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
          data-testid="voice-transcript-preview"
        >
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-emerald-700">
            Transcript saved to notes
          </div>
          {lastTranscript}
        </div>
      )}
    </div>
  );
}
